import { useRef, useState } from "react";
import {
	FaArrowLeft,
	FaFloppyDisk,
	FaMagnifyingGlass,
	FaUser,
	FaUserPlus,
} from "react-icons/fa6";
import "./user-profile-sidemodal.style.scss";
import "./add-employee-wizard.style.scss";

import { useFormik } from "formik";
import * as yup from "yup";

// components
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import { Button } from "../ui/button";
import SelectBox from "../form-inputs/select-box.component";
import TextField from "../form-inputs/text-field.component";
import InputText from "../form-inputs/input-text.component";
import FormError from "../form-error/form-error.component";
import SuccessDisplay from "../success-display/success-display.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import Spinner from "../spinner/spinner.component";

// global strings and functions
import strings from "../../global/strings";
import { PHONE_REGEX, PHONE_REGEX_MESSAGE } from "../../global/phone.const";
import { queryClient } from "../../global/query-client";
import { useDebounce } from "../../hooks/useDebounce";
import {
	Employee,
	FoundUser,
	employeesQueryKey,
	useAddEmployee,
	useFindUsers,
	useCheckUsername,
} from "../../queries/employee.queries";
import { useUploadImage } from "../../queries/image.queries";

interface AddEmployeeWizardProps {
	isOpen: boolean;
	onClose: () => void;
	// Called when "Complete Profile" is picked on the create-mode success
	// screen — the caller (employee-list.page.tsx) uses this to open
	// EmployeeProfile straight into edit mode (startEditable) for the fields
	// this quick-create step deferred (gender/address/city).
	onComplete?: (employee: Employee) => void;
	// Fired instead of the link flow when a search match's in_company is true
	// — that user already has a CompanyUsers row here (active or inactive), so
	// there's nothing to add. Closes the wizard and hands the id to the caller
	// to open that employee's own profile (same handoff shape as onComplete).
	onExistingEmployeeFound?: (userInfoId: string) => void;
}

type WizardMode = "link" | "create" | null;

interface WizardFormValues {
	user_role: "owner" | "user";
	// Single field, split into first_name/last_name only at submit — mirrors
	// CaterMe's add-member-drawer employee_name split (see onSubmit below).
	employee_name: string;
	user_email: string;
	user_mobile: string;
	username: string;
	password: string;
}

const initialValues: WizardFormValues = {
	user_role: "user",
	employee_name: "",
	user_email: "",
	user_mobile: "",
	username: "",
	password: "",
};

const linkSchema = yup.object().shape({
	user_role: yup.string().required("Select a role"),
});

// Quick-create fields only — gender/address/city are deferred to the edit
// pass (see EmployeeProfile's startEditable handoff), matching CaterMe's
// quick-mode member-form-fields split. user_email stays required here (unlike
// CaterMe) since billing_system_BE's User model has it allowNull: false.
const createSchema = yup.object().shape({
	employee_name: yup
		.string()
		.trim()
		.required("Employee name is required")
		.test(
			"has-last-name",
			"Please enter both first and last name",
			(value) =>
				!!value && value.trim().split(/\s+/).filter(Boolean).length >= 2,
		),
	user_mobile: yup
		.string()
		.matches(PHONE_REGEX, PHONE_REGEX_MESSAGE)
		.required("Mobile number is required"),
	user_email: yup
		.string()
		.email("Please enter a valid email")
		.required("Email is required"),
	username: yup
		.string()
		.min(3, "Username must be at least 3 characters")
		.required("Username is required"),
	password: yup
		.string()
		.min(8, "Password must be at least 8 characters")
		.required("Password is required"),
	user_role: yup.string().required("Select a role"),
});

// Two-step add-employee flow: search first for an existing user (across every
// company, via user/find) and link them into this company if found — or fall
// through to creating a brand new user + login. Unlike a plain duplicate
// warning, picking a match here is the intended action (addLogin Path A),
// since one person holding logins in multiple companies is a supported
// feature of the logins-table redesign, not a mistake to steer away from.
function AddEmployeeWizard({
	isOpen,
	onClose,
	onComplete,
	onExistingEmployeeFound,
}: AddEmployeeWizardProps) {
	const [step, setStep] = useState<1 | 2>(1);
	const [mode, setMode] = useState<WizardMode>(null);
	const [linkTarget, setLinkTarget] = useState<FoundUser | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [imageSizeNotice, setImageSizeNotice] = useState<string | null>(null);
	// Holds the newly added/linked employee between success and whichever of
	// the success screen's buttons the user picks.
	const [createdEmployee, setCreatedEmployee] = useState<Employee | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const addEmployeeMutation = useAddEmployee();
	const uploadImageMutation = useUploadImage();

	const debouncedQuery = useDebounce(searchQuery, 400);
	const { data: foundUsers = [], isFetching: isFindingUsers } =
		useFindUsers(debouncedQuery);

	const formik = useFormik<WizardFormValues>({
		validationSchema: mode === "link" ? linkSchema : createSchema,
		initialValues,
		onSubmit: async (values) => {
			// Guards the same form instance's Enter-key submit while still on
			// step 1 (a pure search step, nothing to submit yet).
			if (step === 1 || addEmployeeMutation.isPending) return;

			let response;
			try {
				if (mode === "link" && linkTarget) {
					response = await addEmployeeMutation.mutateAsync({
						user_info_id: linkTarget.user_info_id,
						user_role: values.user_role,
					});
				} else {
					// Split the single Employee Name field into first/last only
					// when building the payload — never shown as two fields.
					const nameTokens = values.employee_name.trim().split(/\s+/).filter(Boolean);
					const [firstToken, ...restTokens] = nameTokens;
					response = await addEmployeeMutation.mutateAsync({
						first_name: firstToken,
						last_name: restTokens.join(" "),
						user_email: values.user_email,
						user_mobile: values.user_mobile,
						username: values.username,
						password: values.password,
						user_role: values.user_role,
					});
				}
			} catch {
				return; // FormError (bound to addEmployeeMutation) renders the message
			}

			// Image upload failure doesn't block employee creation — the
			// employee is already saved, only the avatar is missing.
			if (mode === "create" && selectedImage && response?.result?.user_info_id) {
				await uploadImageMutation
					.mutateAsync({
						image: selectedImage,
						id: response.result.user_info_id,
						type: "user",
					})
					.catch((): undefined => undefined);
			}

			queryClient.invalidateQueries({ queryKey: employeesQueryKey });

			// Drives the success screen below — Complete Profile (create mode
			// only) / Done, no more auto-close timeout.
			if (response?.result) setCreatedEmployee(response.result);
		},
	});

	const debouncedUsername = useDebounce(formik.values.username, 400);
	const { data: usernameCheck, isFetching: isCheckingUsername } =
		useCheckUsername(debouncedUsername);

	const handleClose = () => {
		addEmployeeMutation.reset();
		uploadImageMutation.reset();
		formik.resetForm();
		setStep(1);
		setMode(null);
		setLinkTarget(null);
		setSearchQuery("");
		setSelectedImage(null);
		setImageSizeNotice(null);
		setCreatedEmployee(null);
		onClose();
	};

	const handleSkipForNow = () => {
		handleClose();
	};

	const handleCompleteProfile = () => {
		if (createdEmployee) onComplete?.(createdEmployee);
		handleClose();
	};

	const handleSelectExisting = (user: FoundUser) => {
		// Already has a CompanyUsers row here (active or inactive) — addLogin
		// would either 400 (active) or silently reactivate them (inactive) as a
		// side effect of "linking." Neither belongs behind a search-and-add
		// flow; hand off to the profile instead and let the user decide there
		// (including reactivating, once the profile exposes that directly).
		if (user.in_company) {
			onExistingEmployeeFound?.(user.user_info_id);
			handleClose();
			return;
		}
		setLinkTarget(user);
		setMode("link");
		formik.setFieldValue("user_role", "user");
		setStep(2);
	};

	const handleCreateNew = () => {
		setLinkTarget(null);
		setMode("create");
		setStep(2);
	};

	const handleBack = () => {
		setStep(1);
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		if (selectedFile && selectedFile.size <= 2048576) {
			setSelectedImage(selectedFile);
		} else {
			setImageSizeNotice("File size exceeds the limit");
		}
	};

	const stepDescription =
		step === 1
			? "Step 1 of 2 — Search & Verify"
			: mode === "link"
				? "Step 2 of 2 — Confirm Role"
				: "Step 2 of 2 — Employee Details";

	const showSuccess = Boolean(createdEmployee);

	function _renderSearchStep() {
		const trimmedQuery = debouncedQuery.trim();
		return (
			<div className="user-search-wizard">
				<p className="wizard-hint">
					Search by name, mobile, email, or username to add someone who
					already has an account in another company — or skip ahead and
					create a brand new user.
				</p>
				<InputText
					label="Search"
					icon={<FaMagnifyingGlass />}
					placeholder="Type at least 2 characters..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
				/>

				{trimmedQuery.length >= 2 && (
					<div className="user-match-list">
						{isFindingUsers ? (
							<div className="user-match-hint">
								<Spinner styles={undefined} />
								<span>Searching...</span>
							</div>
						) : foundUsers.length === 0 ? (
							<div className="user-match-hint">
								No matching users found. You can create a new one below.
							</div>
						) : (
							<>
								<p className="user-match-list-label">
									Existing users you can add to this company
								</p>
								{foundUsers.map((user) => (
									<button
										type="button"
										key={user.user_info_id}
										className="user-match-item"
										onClick={() => handleSelectExisting(user)}
									>
										<div className="user-match-avatar">
											{user.user_image ? (
												<img
													crossOrigin="anonymous"
													src={user.user_image}
													alt=""
												/>
											) : (
												<FaUser />
											)}
										</div>
										<div className="user-match-info">
											<span className="user-match-name-row">
												<span className="user-match-name cap-display">
													{user.first_name} {user.last_name}
												</span>
												{user.in_company && (
													<span className="user-match-badge">
														Already added
													</span>
												)}
											</span>
											<span className="user-match-subtitle">
												{[
													user.logins?.[0]?.username
														? `@${user.logins[0].username}`
														: null,
													user.user_mobile,
													user.user_email,
												]
													.filter(Boolean)
													.join(" · ")}
											</span>
										</div>
										<span className="user-match-action">
											{user.in_company ? "View Profile" : "Add"}
										</span>
									</button>
								))}
							</>
						)}
					</div>
				)}

				<div className="wizard-divider-row">
					<span>or</span>
				</div>

				<Button type="button" variant="outline" onClick={handleCreateNew}>
					<FaUserPlus /> Create New User
				</Button>
			</div>
		);
	}

	function _renderLinkStep() {
		return (
			<div className="user-info-container">
				<h2 className="section-header">Add Existing User</h2>
				<div className="user-match-item selected">
					<div className="user-match-avatar">
						{linkTarget?.user_image ? (
							<img crossOrigin="anonymous" src={linkTarget.user_image} alt="" />
						) : (
							<FaUser />
						)}
					</div>
					<div className="user-match-info">
						<span className="user-match-name cap-display">
							{linkTarget?.first_name} {linkTarget?.last_name}
						</span>
						<span className="user-match-subtitle">
							{[
								linkTarget?.logins?.[0]?.username
									? `@${linkTarget.logins[0].username}`
									: null,
								linkTarget?.user_mobile,
								linkTarget?.user_email,
							]
								.filter(Boolean)
								.join(" · ")}
						</span>
					</div>
				</div>
				<div className="input-wrapper">
					<label className="input-label">Role</label>
					<SelectBox
						name="user_role"
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						value={formik.values.user_role}
						options={strings.role}
					/>
					{formik.submitCount > 0 && (
						<div className="validation-error">{formik.errors.user_role}</div>
					)}
				</div>
			</div>
		);
	}

	function _renderCreateStep() {
		const trimmedUsername = formik.values.username.trim();
		return (
			<>
				<div
					className={`user-img-container editable`}
					onClick={() => fileInputRef.current?.click()}
				>
					<div className="item_img">
						{selectedImage ? (
							<img
								crossOrigin="anonymous"
								src={URL.createObjectURL(selectedImage)}
								alt="user"
							/>
						) : (
							<FaUser size="6em" />
						)}
					</div>
				</div>
				<input
					type="file"
					accept="image/*"
					ref={fileInputRef}
					style={{ display: "none" }}
					onChange={handleImageChange}
				/>
				<InlineNotice
					message={imageSizeNotice}
					type="warning"
					onDismiss={() => setImageSizeNotice(null)}
				/>

				<div className="user-info-container">
					<h2 className="section-header">Employee Details</h2>
					<TextField
						name="employee_name"
						label="Employee Name"
						placeholder="Enter first and last name"
						formik={formik}
					/>
					<div className="input-row-wrapper">
						<TextField
							name="user_mobile"
							label="Mobile Number"
							placeholder="Enter Mobile Number"
							type="tel"
							formik={formik}
						/>
						<TextField
							name="user_email"
							label="Employee Email"
							placeholder="Enter Email"
							formik={formik}
						/>
					</div>
					<div className="input-wrapper">
						<label className="input-label">Role</label>
						<SelectBox
							name="user_role"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.user_role}
							options={strings.role}
						/>
						{formik.submitCount > 0 && (
							<div className="validation-error">{formik.errors.user_role}</div>
						)}
					</div>
				</div>

				<div className="divider" />

				<div className="user-info-container">
					<h2 className="section-header">Login Details</h2>
					<div className="input-wrapper">
						<label className="input-label">Username</label>
						<input
							name="username"
							className="input-box-wrapper"
							type="text"
							placeholder="Enter username"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.username}
							autoComplete="off"
						/>
						{formik.submitCount > 0 && formik.errors.username ? (
							<div className="validation-error">{formik.errors.username}</div>
						) : (
							trimmedUsername.length >= 3 &&
							!isCheckingUsername &&
							usernameCheck &&
							(usernameCheck.available === true ? (
								<div className="username-hint success">
									Username available
								</div>
							) : usernameCheck.available === false ? (
								<div className="username-hint error">
									{usernameCheck.message || "Username already taken"}
								</div>
							) : null)
						)}
					</div>
					<TextField
						name="password"
						label="Password"
						placeholder="Enter login password"
						formik={formik}
						securetext
					/>
				</div>
			</>
		);
	}

	return (
		<StructuredSideModal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) handleClose();
			}}
			title="Add Employee"
			description={stepDescription}
			width={560}
			isDirty={!showSuccess && step === 2 && formik.dirty}
			onSubmit={formik.handleSubmit}
			footer={
				!showSuccess &&
				step === 2 && (
					<div className="flex justify-between">
						<Button variant="outline" size="sm" type="button" onClick={handleBack}>
							<FaArrowLeft /> Back
						</Button>
						<Button size="sm" type="submit" disabled={addEmployeeMutation.isPending}>
							<FaFloppyDisk />
							{mode === "link" ? "Add to Company" : "Create & Add"}
						</Button>
					</div>
				)
			}
		>
			<div className="user-modal-container">
				<div className="modal-body">
					{showSuccess ? (
						mode === "create" ? (
							<SuccessDisplay
								title="Employee added"
								description="Add more details now, or fill them in anytime from their profile."
							>
								<Button type="button" onClick={handleCompleteProfile}>
									Complete Profile
								</Button>
								<Button
									type="button"
									variant="secondary"
									onClick={handleSkipForNow}
								>
									Skip for now
								</Button>
							</SuccessDisplay>
						) : (
							<SuccessDisplay
								title="Employee added"
								description="Existing user has been added to this company."
							>
								<Button type="button" onClick={handleSkipForNow}>
									Done
								</Button>
							</SuccessDisplay>
						)
					) : (
						<>
							<FormError
								isError={addEmployeeMutation.isError || uploadImageMutation.isError}
								error={addEmployeeMutation.error || uploadImageMutation.error}
								fallback="Something went wrong. Please try again."
							/>
							{step === 1 && _renderSearchStep()}
							{step === 2 && mode === "link" && _renderLinkStep()}
							{step === 2 && mode === "create" && _renderCreateStep()}
						</>
					)}
				</div>
			</div>
		</StructuredSideModal>
	);
}

export default AddEmployeeWizard;
